import React, { useState, useMemo } from "react";
import { Box, Select, MenuItem, SelectChangeEvent, Typography, useMediaQuery } from "@mui/material";
import { LineSeries } from "@nivo/line";
import { useTheme } from "@mui/material/styles";
import { area, curveMonotoneX } from "d3-shape";
import dayjs from "dayjs";
import { useCompanyPERatioHistory } from "../hooks/useCompanyPERatioHistory";
import { getChartColors } from "../constants/chartColors";
import { evenlySpacedTickValues } from "../utils/evenlySpacedTickValues";
import GenericFinancialLineChart from "./shared/GenericFinancialLineChart";
import ChartTooltip from "./shared/ChartTooltip";
import { hapticSelection } from "../../../../lib/haptics";

const createPEAreaLayer = (color: string) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PEAreaLayer = ({ series, innerHeight, innerWidth }: any) => {
    const instanceId = React.useId().replace(/:/g, "");

    if (!series || series.length === 0) return null;

    const serie = series[0];
    const points = serie.data
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((d: any) => d.position.x != null && d.position.y != null)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((d: any) => ({
        x: d.position.x as number,
        y: d.position.y as number,
      }));

    if (points.length < 2) return null;

    const clipId = `pe-area-clip-${instanceId}`;
    const gradientId = `pe-area-gradient-${instanceId}`;

    const areaGen = area<{ x: number; y: number }>()
      .x((d) => d.x)
      .y1((d) => d.y)
      .y0(innerHeight)
      .curve(curveMonotoneX);

    const path = areaGen(points);
    if (!path) return null;

    return (
      <g>
        <defs>
          <clipPath id={clipId}>
            <rect x={0} y={0} width={innerWidth} height={innerHeight} />
          </clipPath>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.18} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <path d={path} fill={`url(#${gradientId})`} />
        </g>
      </g>
    );
  };
  return PEAreaLayer;
};

const YEAR_OPTIONS = [
  { label: "1 Year", value: 1 },
  { label: "2 Years", value: 2 },
  { label: "3 Years", value: 3 },
  { label: "5 Years", value: 5 },
  { label: "10 Years", value: 10 },
  { label: "15 Years", value: 15 },
  { label: "20 Years", value: 20 },
];

const DEFAULT_YEARS = 1;

interface HistoricPERatioChartProps {
  ticker: string;
  height?: number;
  isModal?: boolean;
  onCloseModal?: () => void;
  renderCard?: boolean;
  showFullscreen?: boolean;
  noHover?: boolean;
}

const HistoricPERatioChart: React.FC<HistoricPERatioChartProps> = ({
  ticker,
  height = 320,
  isModal = false,
  onCloseModal,
  renderCard = true,
  showFullscreen = false,
  noHover = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const peColor = getChartColors(isDark).positive;

  const [years, setYears] = useState(DEFAULT_YEARS);

  const { data: response, isLoading, error } = useCompanyPERatioHistory(ticker, years);

  const chartData = useMemo((): LineSeries[] => {
    if (!response?.data?.length) return [];

    return [
      {
        id: "PE Ratio",
        color: peColor,
        data: response.data.map((point) => ({
          x: dayjs(point.date).format("DD MMM YYYY"),
          y: point.peRatio,
        })),
      } as LineSeries,
    ];
  }, [response, peColor]);

  const PEAreaLayer = useMemo(() => createPEAreaLayer(peColor), [peColor]);

  const currentPERatio = response?.data?.[response.data.length - 1]?.peRatio;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CurrentPELineLayer = useMemo(() => ({ yScale, innerWidth }: any) => {
    if (currentPERatio == null || typeof yScale !== "function") return null;

    const y = yScale(currentPERatio);
    if (!Number.isFinite(y)) return null;

    const labelColor = theme.palette.text.primary;
    const lineColor = theme.palette.text.secondary;

    return (
      <g>
        <line
          x1={0}
          x2={innerWidth}
          y1={y}
          y2={y}
          stroke={lineColor}
          strokeWidth={1}
          strokeDasharray="4 4"
          opacity={0.6}
        />
        <text
          x={innerWidth + 6}
          y={y + 3}
          textAnchor="start"
          fontSize={11}
          fontWeight={600}
          fill={labelColor}
        >
          {`${currentPERatio.toFixed(1)}x`}
        </text>
      </g>
    );
  }, [currentPERatio, theme.palette.text.primary, theme.palette.text.secondary]);

  const axisBottomTickValues = useMemo(() => {
    const xValues = chartData[0]?.data.map((d) => String(d.x)) ?? [];
    if (xValues.length === 0) return undefined;
    return evenlySpacedTickValues(xValues, isMobile);
  }, [chartData, isMobile]);

  const isEmpty = chartData.length === 0 || chartData[0].data.length === 0;

  const handleYearsChange = (event: SelectChangeEvent<number>) => {
    hapticSelection();
    setYears(event.target.value as number);
  };

  const hoverBg = isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)";

  const periodSelector = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        mt: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          color: theme.palette.text.secondary,
        }}
      >
        Show
      </Typography>
      <Select
        value={years}
        onChange={handleYearsChange}
        size="small"
        inputProps={{ "aria-label": "P/E history range" }}
        sx={{
          minWidth: 130,
          height: 34,
          "@media (pointer: coarse)": { height: 50 },
          backgroundColor: "background.surface",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          "& .MuiSelect-select": {
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
            py: 0.5,
            px: 1.5,
          },
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&:hover": {
            backgroundColor: hoverBg,
            borderColor: isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.15)",
          },
          "&.Mui-focused": {
            borderColor: theme.palette.primary.main,
            backgroundColor: "background.surface",
          },
          "& .MuiSelect-icon": { color: theme.palette.text.secondary },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              mt: 0.5,
              boxShadow: "none",
              "& .MuiMenuItem-root": {
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: theme.palette.text.primary,
                py: 1,
                px: 1.5,
                minHeight: "auto",
                "&:hover": { backgroundColor: hoverBg },
                "&.Mui-selected": {
                  backgroundColor: hoverBg,
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
            },
          },
        }}
      >
        {YEAR_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  const tooltipRenderer = (point: {
    data: { xFormatted: string | number; y: number };
    serieColor: string;
  }) => (
    <ChartTooltip
      header={String(point.data.xFormatted)}
      rows={[
        {
          label: "PE Ratio",
          value: `${(point.data.y ?? 0).toFixed(1)}x`,
          color: peColor,
        },
      ]}
    />
  );

  return (
    <GenericFinancialLineChart
      title="Historic PE Ratio (TTM)"
      ticker={ticker}
      height={height}
      isModal={isModal}
      renderCard={renderCard}
      showFullscreen={showFullscreen}
      noHover={noHover}
      onCloseModal={onCloseModal}
      isLoading={isLoading}
      error={error ?? null}
      chartData={chartData}
      isEmpty={isEmpty}
      enableCrosshair={false}
      lineWidth={2.5}
      pointSize={0}
      useSlices={true}
      customLayers={[PEAreaLayer, CurrentPELineLayer]}
      bottomMargin={isMobile ? 70 : 80}
      rightMargin={48}
      axisBottomTickPadding={isMobile ? 14 : 16}
      axisLeftTickPadding={isMobile ? 12 : 10}
      axisBottomTickValues={axisBottomTickValues}
      yAxisLegend="PE Ratio"
      yAxisFormat={(value) => `${Number(value).toFixed(1)}x`}
      colors={() => peColor}
      tooltip={tooltipRenderer}
      belowChartContent={periodSelector}
      noDataTitle="No PE Ratio Data Available"
      noDataDescription="A daily PE ratio cannot be computed for this company. This can occur when price or earnings data is unavailable for the selected period."
    />
  );
};

export default HistoricPERatioChart;
